/**
 * Full-screen value-vote overlay.
 *
 * Shown after a category wins: the remaining dollar values for that
 * category render as big vote cards and viewers type 1-N (or the dollar
 * amount directly — the backend accepts both) to pick the value.
 *
 * Mirrors the category-vote overlay (ui/category-vote.ts): top-left
 * circular selection badge, bottom-right "VOTES" chip, drop shadow,
 * leader yellow border + glow, winner solid fill. Uses a different ROOT
 * id so styles don't collide.
 *
 * Lifecycle: created once at boot; open()/close() toggles visibility +
 * rebuilds the cards each time. setTallies() tween-updates the count chips;
 * setWinner() freezes the winner card.
 */

import type { PollOption } from '@sg/shared';
import { fitText, unfitText } from './fit-text.js';

export interface ValueVoteHandle {
  el: HTMLDivElement;
  open: (options: PollOption[]) => void;
  close: () => void;
  setTallies: (tallies: Record<string, number>) => void;
  setWinner: (key: string | null) => void;
  dispose: () => void;
}

interface CardState {
  key: string;
  card: HTMLDivElement;
  label: HTMLDivElement;
  chip: HTMLDivElement;
  chipCount: HTMLSpanElement;
  index: number;
  used: boolean;
}

const ROOT_ID = 'sg-value-vote';

const STYLES = `
  @keyframes sg-valvote-in {
    from { opacity: 0; transform: translateY(16px) scale(0.96); }
    to   { opacity: 1; transform: translateY(0)    scale(1);    }
  }
  @keyframes sg-valvote-leader {
    0%, 100% { box-shadow: 0 0 0 rgba(255,196,61,0.0), 0 12px 32px rgba(0,0,0,0.5); }
    50%      { box-shadow: 0 0 32px rgba(255,196,61,0.55), 0 12px 32px rgba(0,0,0,0.5); }
  }
  .sg-valvote {
    position: absolute;
    left: 0;
    top: 0;
    /* See category-vote.ts: right 420 -> 540 (Apr 2026) so the grid centre
       sits 60px left of the old position, matching the leftward board shift. */
    right: 540px;
    bottom: 0;
    z-index: 40;
    display: none;
    align-items: center;
    justify-content: center;
    background: transparent;
    pointer-events: none;
  }
  .sg-valvote.visible { display: flex; }
  .sg-valvote-grid {
    display: grid;
    /* 1-5 options per round (shrink-as-used); use up-to-5 column layout.
       Grid auto-balances a short row of cards across the centre. */
    grid-auto-flow: column;
    grid-template-rows: 1fr;
    gap: 24px;
    /* Trimmed 1280→1180 (Apr 2026) so the leader scale(1.02)/winner
       scale(1.04) cards never bleed past x=1500 into the right-side
       leaderboard area. See category-vote.ts for the same change. */
    width: 1180px;
    max-width: 1180px;
    height: 540px;
    padding: 20px;
    justify-content: center;
    align-items: stretch;
  }
  .sg-valvote-card {
    position: relative;
    width: 220px;
    background: linear-gradient(180deg, rgba(6,12,233,0.92) 0%, rgba(3,5,55,0.95) 100%);
    border: 3px solid rgba(255,196,61,0.3);
    border-radius: 14px;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px 20px;
    box-shadow: 0 12px 32px rgba(0,0,0,0.5);
    animation: sg-valvote-in 320ms cubic-bezier(.22,.9,.36,1) backwards;
    transition: border-color 180ms ease-out, transform 180ms ease-out;
  }
  .sg-valvote-card.leader {
    border-color: var(--sg-primary, #FFC43D);
    animation: sg-valvote-in 320ms cubic-bezier(.22,.9,.36,1) backwards,
               sg-valvote-leader 1.8s ease-in-out infinite 320ms;
    transform: scale(1.02);
  }
  .sg-valvote-card.winner {
    border-color: var(--sg-primary, #FFC43D);
    background: linear-gradient(180deg, rgba(255,196,61,0.25) 0%, rgba(6,12,233,0.95) 100%);
    transform: scale(1.04);
  }
  .sg-valvote-card.used {
    /* Used slots stay visible so number badges remain stable, but render
       muted so viewers can see they're spent (and the leader/winner glow
       can't fire on them). Border + chip + label all dim together. */
    opacity: 0.32;
    filter: grayscale(0.4);
    border-color: rgba(255,255,255,0.18);
    box-shadow: none;
  }
  .sg-valvote-card.used .sg-valvote-chip {
    display: none;
  }
  .sg-valvote-label {
    font-family: 'Oswald','Bebas Neue','Impact',sans-serif;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-align: center;
    line-height: 1.0;
    color: var(--sg-primary, #FFC43D);
    text-shadow: 0 3px 10px rgba(0,0,0,0.7), 0 0 18px rgba(255,196,61,0.35),
                 2px 2px 0 #000, -2px -2px 0 #000, 2px -2px 0 #000, -2px 2px 0 #000;
    width: 100%;
    height: 100%;
    padding: 14px 6px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .sg-valvote-index {
    position: absolute;
    top: 14px;
    left: 14px;
    width: 72px;
    height: 72px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Oswald','Bebas Neue','Impact',sans-serif;
    font-size: 52px;
    font-weight: 700;
    line-height: 1;
    color: var(--sg-text-on-primary, #060CE9);
    background: var(--sg-primary, #FFC43D);
    border-radius: 50%;
    box-shadow: 0 6px 16px rgba(0,0,0,0.5), 0 0 0 3px rgba(3,5,55,0.9);
    text-shadow: none;
  }
  .sg-valvote-chip {
    position: absolute;
    bottom: 14px;
    right: 14px;
    font-family: 'Oswald','Bebas Neue','Impact',sans-serif;
    font-weight: 700;
    font-size: 34px;
    line-height: 1;
    color: var(--sg-primary, #FFC43D);
    background: rgba(0,0,0,0.55);
    border: 2px solid rgba(255,196,61,0.55);
    padding: 6px 14px 4px 14px;
    border-radius: 14px;
    min-width: 60px;
    text-align: center;
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 6px;
    letter-spacing: 0.04em;
  }
  .sg-valvote-chip-label {
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.78);
  }
  .sg-valvote-card.leader .sg-valvote-chip {
    background: var(--sg-primary, #FFC43D);
    color: var(--sg-text-on-primary, #060CE9);
    border-color: var(--sg-primary, #FFC43D);
  }
  .sg-valvote-card.leader .sg-valvote-chip-label {
    color: rgba(6,12,233,0.75);
  }
`;

/**
 * Mount the value-vote overlay inside `stageEl` so it scales with the rest
 * of the 1920×1080 DOM stage.
 */
export function createValueVote(stageEl: HTMLElement): ValueVoteHandle {
  if (!document.getElementById(`${ROOT_ID}-styles`)) {
    const style = document.createElement('style');
    style.id = `${ROOT_ID}-styles`;
    style.textContent = STYLES;
    document.head.appendChild(style);
  }

  const el = document.createElement('div');
  el.id = ROOT_ID;
  el.className = 'sg-valvote';
  el.setAttribute('data-testid', 'value-vote');
  stageEl.appendChild(el);

  const grid = document.createElement('div');
  grid.className = 'sg-valvote-grid';
  el.appendChild(grid);

  let cards: CardState[] = [];
  let winner: string | null = null;
  let tallies: Record<string, number> = {};

  function clearCards(): void {
    for (const c of cards) unfitText(c.label);
    grid.innerHTML = '';
    cards = [];
  }

  function renderCard(opt: PollOption, i: number): CardState {
    const card = document.createElement('div');
    card.className = 'sg-valvote-card';
    if (opt.used) card.classList.add('used');
    card.style.animationDelay = `${i * 60}ms`;

    const idx = document.createElement('div');
    idx.className = 'sg-valvote-index';
    idx.textContent = String(i + 1);
    card.appendChild(idx);

    const label = document.createElement('div');
    label.className = 'sg-valvote-label';
    // Label is the point value shown to the viewer (no currency prefix).
    label.textContent = String(opt.label);
    card.appendChild(label);

    const chip = document.createElement('div');
    chip.className = 'sg-valvote-chip';
    const chipCount = document.createElement('span');
    chipCount.className = 'sg-valvote-chip-count';
    chipCount.textContent = '0';
    const chipLabel = document.createElement('span');
    chipLabel.className = 'sg-valvote-chip-label';
    chipLabel.textContent = 'VOTES';
    chip.appendChild(chipCount);
    chip.appendChild(chipLabel);
    card.appendChild(chip);

    grid.appendChild(card);

    requestAnimationFrame(() => {
      fitText(label, { min: 20, max: 110, lineHeight: 1.0 });
    });

    return { key: opt.key, card, label, chip, chipCount, index: i, used: opt.used === true };
  }

  function open(options: PollOption[]): void {
    clearCards();
    winner = null;
    tallies = {};
    for (let i = 0; i < options.length; i++) {
      const opt = options[i];
      if (opt) cards.push(renderCard(opt, i));
    }
    el.classList.add('visible');
    applyLeaderStyling();
  }

  function close(): void {
    el.classList.remove('visible');
    setTimeout(() => {
      if (!el.classList.contains('visible')) {
        clearCards();
        winner = null;
        tallies = {};
      }
    }, 400);
  }

  function setTallies(next: Record<string, number>): void {
    tallies = next;
    for (const c of cards) {
      const v = next[c.key] ?? 0;
      c.chipCount.textContent = String(v);
    }
    applyLeaderStyling();
  }

  function setWinner(key: string | null): void {
    winner = key;
    applyLeaderStyling();
  }

  function applyLeaderStyling(): void {
    let top = 0;
    for (const c of cards) {
      if (c.used) continue;
      const v = tallies[c.key] ?? 0;
      if (v > top) top = v;
    }
    for (const c of cards) {
      c.card.classList.remove('leader', 'winner');
      if (c.used) continue;
      if (winner !== null) {
        if (c.key === winner) c.card.classList.add('winner');
      } else if (top > 0 && (tallies[c.key] ?? 0) === top) {
        c.card.classList.add('leader');
      }
    }
  }

  return {
    el,
    open,
    close,
    setTallies,
    setWinner,
    dispose(): void {
      clearCards();
      el.remove();
    },
  };
}
